import type { ReportCanvasLayout } from '@erp/shared-types';
import { rowsToGrid } from './grid';
import { compactLayout, newNodeId } from './layout';
import { componentSize, type ComponentRegistry } from './registry';
import type { GridLayout, GridRect, ReportComponentConfig } from './types';

/**
 * Adapters between the canvas runtime layout (`GridLayout` — items on the
 * Report grid, each with a drag-and-drop id) and the serialized form persisted
 * everywhere (`{ items }`: each item's place and size, and its component
 * config; see `ReportCanvasLayoutSchema` in `@erp/shared-types`). Item ids are
 * runtime-only and regenerated on load.
 *
 * Both the org/user canvas-config endpoints and the query-builder report
 * library persist this shape.
 */

// A type alias (not an interface) on purpose: aliases get an implicit index
// signature, so serialized layouts assign directly to the loose
// Record<string, unknown> shapes the canvas-AI contracts use on the wire.
export type SerializedCanvasLayout = {
  items: (GridRect & { component: ReportComponentConfig })[];
};

/** Runtime layout → serialized form (drops item ids), in reading order. */
export function serializeLayout(layout: GridLayout): SerializedCanvasLayout {
  return {
    items: [...layout]
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map(({ x, y, w, h, component }) => ({ x, y, w, h, component })),
  };
}

/** A saved layout's items on the grid; rows saved before it are converted. */
function savedItems(
  serialized: ReportCanvasLayout | SerializedCanvasLayout,
  registry: ComponentRegistry,
): (GridRect & { component: ReportComponentConfig })[] {
  if ('items' in serialized) return serialized.items;
  return rowsToGrid(
    serialized.data,
    (component) => componentSize(registry, component.type).h,
  );
}

/**
 * Serialized form → runtime layout, compacted on the grid, each item at least
 * its type's smallest size. A layout saved as rows (before the grid) converts
 * row by row, each row as tall as its tallest component's default height.
 *
 * Ids are regenerated fresh so a loaded layout never collides with the
 * session's node ids; each item id matches its component id (the `makeItem`
 * convention). `keepComponentIds` keeps each component's saved id instead (a
 * fresh one only for a missing or repeated id), for a host that reloads a
 * layout it saved in the same session — an undo — and wants the same
 * components to stay selected.
 */
export function deserializeLayout(
  serialized: ReportCanvasLayout | SerializedCanvasLayout,
  registry: ComponentRegistry,
  { keepComponentIds = false }: { keepComponentIds?: boolean } = {},
): GridLayout {
  const seen = new Set<string>();
  const idFor = (saved: string) => {
    const id =
      keepComponentIds && saved.length > 0 && !seen.has(saved)
        ? saved
        : newNodeId();
    seen.add(id);
    return id;
  };
  return compactLayout(
    savedItems(serialized, registry).map(({ component, ...rect }) => {
      const id = idFor(component.id);
      const { minW, minH } = componentSize(registry, component.type);
      return {
        ...rect,
        w: Math.max(minW, rect.w),
        h: Math.max(minH, rect.h),
        id,
        component: { ...component, id },
      };
    }),
  );
}
